<svelte:options runes={true} />

<script lang="ts">
  import { on } from "svelte/events";
  import { fade } from "svelte/transition";
  import { backOut, cubicOut } from "svelte/easing";
  import { motionDuration } from "~/lib/motion-duration";
  import { dropFromHeader } from "~/lib/search/drop-from-header";
  import { createSearchShell } from "~/lib/search/create-search-shell.svelte";
  import Input from "~/components/ui/Input/Input.svelte";
  import AutocorrectNotice from "~/components/search/AutocorrectNotice/AutocorrectNotice.svelte";
  import PopularSearchList from "~/components/search/PopularSearchList/PopularSearchList.svelte";
  import ProductSuggestionList from "~/components/search/ProductSuggestionList/ProductSuggestionList.svelte";
  import type { createSearchAutosuggestCore } from "~/lib/search/create-search-autosuggest-core.svelte";

  type SearchAutosuggestCore = ReturnType<typeof createSearchAutosuggestCore>;

  interface Props {
    core: SearchAutosuggestCore;
    onSubmit: (query: string) => void;
    class?: string;
  }

  let { core, onSubmit, class: className = "" }: Props = $props();

  const PANEL_ID = "da-desktop-search-panel";

  // ProductMiniCard's vertical card width and ProductSuggestionList's row gap.
  const CARD_WIDTH_PX = 175;
  const CARD_GAP_PX = 12;

  // Five text rows is what the terms column has room for beside a single row of cards.
  const SUGGESTION_LIMIT = 5;

  let shellEl: HTMLDivElement | undefined = $state();

  // `flex: 1; min-width: 0` beside a fixed-width terms column, so this width is a
  // function of the panel's and never of how many cards are in it — measuring it to
  // decide the card count can't feed back on itself.
  let productsWidth = $state(0);

  // Undefined until measured, so the core starts uncapped rather than capped at zero.
  const fitCount = $derived(
    productsWidth > 0
      ? Math.max(1, Math.floor((productsWidth + CARD_GAP_PX) / (CARD_WIDTH_PX + CARD_GAP_PX)))
      : undefined,
  );

  $effect(() => {
    core.setVisibleProductCount(fitCount);
  });

  $effect(() => {
    core.setVisibleSuggestionCount(SUGGESTION_LIMIT);
  });

  // Non-modal: the panel never traps focus and never portals, so dismissal is ours
  // to own. A modal Dialog here stole focus on the second keystroke — see the
  // 2026-09-01 audit, §1.1.
  $effect(() => {
    if (!core.panelVisible) return;
    return on(document, "pointerdown", (event) => {
      if (!shellEl?.contains(event.target as Node)) core.dismiss();
    });
  });

  // `position: fixed` means the panel's top is a viewport coordinate, so it has to be
  // measured rather than expressed as `100%` — off the shell's own rect, which keeps
  // this free of any theme selector. Re-measured while open: the header moves.
  $effect(() => {
    if (!core.panelVisible || !shellEl) return;
    const el = shellEl;
    const place = () => {
      const panelTop = Math.round(el.getBoundingClientRect().bottom + 8);
      el.style.setProperty("--da-search-panel-top", `${panelTop}px`);
      // The scrim starts where the header ends, so the header stays legible above it.
      // `<header>` rather than a theme class; without one the scrim meets the panel.
      const header = el.closest("header")?.getBoundingClientRect().bottom;
      el.style.setProperty("--da-search-scrim-top", `${Math.round(header ?? panelTop)}px`);
    };
    place();
    const offScroll = on(window, "scroll", place, { passive: true });
    const offResize = on(window, "resize", place, { passive: true });
    return () => {
      offScroll();
      offResize();
    };
  });

  const shell = createSearchShell({
    get core() {
      return core;
    },
    get onSubmit() {
      return onSubmit;
    },
    // preventDefault on both steps, so the browser's own Escape handling (a form
    // reset, on some engines) never races the one that clears the field.
    onEscape(event) {
      event.preventDefault();
      core.escape();
    },
  });
</script>

<!-- Tab out and nothing else would dismiss the panel: Escape is bound to the field, and
     the scrim now covers the page the visitor just tabbed into. `relatedTarget` has to be
     a real node, or alt-tabbing away from the window would close it too. -->
<div
  class={["da-desktop-search-shell", className]}
  bind:this={shellEl}
  onfocusout={(event) => {
    if (event.relatedTarget && !shellEl?.contains(event.relatedTarget as Node)) core.dismiss();
  }}
>
  <div class="da-sr-only" role="status">{shell.status}</div>
  <!-- The design-system field, not a bare input: it brings the icon, focus ring and
       radius. `type` is forced back to text because a native search input clears itself
       on Escape, firing an empty query on the keystroke that is meant to dismiss the
       panel and keep what was typed. -->
  <Input
    variant="search"
    type="text"
    name="q"
    role="combobox"
    aria-label="Search"
    placeholder="Search"
    class="da-desktop-search-shell__input"
    value={core.displayValue}
    aria-expanded={shell.listboxVisible}
    aria-controls={shell.listboxVisible ? PANEL_ID : undefined}
    aria-activedescendant={shell.listboxVisible ? core.highlightedItemId : undefined}
    aria-autocomplete="list"
    autocomplete="off"
    oninput={shell.handleInput}
    onkeydown={shell.handleKeydown}
    onfocus={() => core.reopen()}
  />

  {#if core.panelVisible}
    <!-- Presentational only: the panel is non-modal, so this dims and blocks the page
         without trapping focus. Escape and an outside pointerdown still dismiss. -->
    <div
      class="da-desktop-search-shell__scrim"
      aria-hidden="true"
      onpointerdown={() => core.dismiss()}
      transition:fade|global={{ duration: motionDuration(350), easing: cubicOut }}
    ></div>
    <!-- `|global` is load-bearing. Transitions are local by default, and a local one
         plays only when its own block is created, not when a parent block is — so a
         story or test fixture that renders with the panel already open (autosuggest
         itself never opens on mount — seedDisplayValue never arms the debounced query)
         would otherwise animate nothing. -->
    <div
      class="da-desktop-search-shell__panel"
      transition:dropFromHeader|global={{ y: -80, duration: motionDuration(350), easing: backOut }}
    >
      {#if core.isEmpty}
        <p class="da-desktop-search-shell__empty">
          No results for “{core.displayValue}”
        </p>
      {:else}
        {#if core.autocorrectQuery}
          <AutocorrectNotice query={core.autocorrectQuery} />
        {/if}
        <!-- One set of elements across loading and loaded, so nothing moves when results
             land and the measured column width survives. role="listbox" only appears once
             there are real options: skeletons are not options, and an empty listbox fails
             aria-required-children. -->
        <div
          id={shell.listboxVisible ? PANEL_ID : undefined}
          role={shell.listboxVisible ? "listbox" : undefined}
          aria-label={shell.listboxVisible ? "Search suggestions" : undefined}
          class="da-desktop-search-shell__columns"
        >
          <!-- Products first — core.items' order, which the shared item ids make binding
               on DOM order too (WCAG 1.3.2, meaningful sequence). -->
          <div class="da-desktop-search-shell__products" bind:clientWidth={productsWidth}>
            <ProductSuggestionList
              items={shell.productItems}
              showAll={shell.showAllItem}
              highlightedItemId={core.highlightedItemId}
              vertical
              skeleton={core.isFetching}
              skeletonCount={fitCount}
            />
          </div>
          <PopularSearchList
            class="da-desktop-search-shell__terms"
            items={shell.suggestionItems}
            highlightedItemId={core.highlightedItemId}
            onSelectTerm={shell.selectTerm}
            skeleton={core.isFetching}
          />
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  @layer aurora {
    .da-desktop-search-shell {
      position: relative;
      width: 100%;
    }

    /* Input brings its own border, radius, padding and focus ring — the shell only
       has to say how wide it is. */
    :global(.da-desktop-search-shell__input) {
      width: 100%;
    }

    /* Figma 9961:2106: the panel is the width of the page-width container and aligned
       with the content under it, not with the ~278px field it hangs from. `fixed` is
       what reaches the viewport's page-width box while staying a DOM child of the shell,
       which the outside-pointerdown dismissal relies on. It resolves against the viewport
       rather than .da-root because this island opts out of containment — ADR 0004. */
    .da-desktop-search-shell__panel {
      /* Extra panel above the resting top edge, so `backOut`'s overshoot slides more
         panel into view rather than opening a gap under the header — the same offset-and-
         pad-back trick as .da-slide-over-dialog__panel--top. There the bleed hides off
         screen; here `clip-path` below plays the part of the screen edge. The overshoot
         measures ~8px on the 80px fly, so 16px is double the margin needed. */
      --da-search-panel-bleed: var(--da-space-4);

      display: flex;
      flex-direction: column;
      gap: var(--da-space-4);

      position: fixed;
      /* Set per open from the shell's rect. The fallback means "unplaced", not "just
         below the field" — on a fixed element 100% resolves against the viewport. */
      top: calc(var(--da-search-panel-top, 100%) - var(--da-search-panel-bleed));

      /* Full-bleed surface, page-aligned contents — the same split ActiveFilters uses
         for its own full-width row. The panel hangs off the header and reads wrong
         inset from the viewport edges, but its contents still have to line up with the
         page box underneath, so the two theme co-ordination points moved from `width`
         to `padding-inline` below rather than going away. */
      left: 0;
      right: 0;
      /* Down to (near) the viewport floor rather than off the bottom of it. The bleed
         is not subtracted back, so this stops one bleed short — which is the gap we
         want under it anyway. */
      max-height: calc(100dvh - var(--da-search-panel-top, 0px));
      /* `hidden` on the inline axis: the columns clip themselves, and an `auto` there
         would put a horizontal scrollbar under a panel this wide for a stray pixel. */
      overflow: hidden auto;
      z-index: 20;
      background: var(--da-color-brand-sand-500);
      /* Bottom corners only — the panel reads as hanging off the header. */
      border-radius: 0 0 var(--da-radius-lg) var(--da-radius-lg);
      box-shadow: var(--da-shadow-md);
      /* The bleed is added back to the top so content sits where the design puts it.

         The `max()` is the page box: `50% - max-width / 2` is the page container's own
         left edge once the viewport is wider than the cap, and the section padding is
         what that collapses to below it — so the panel's contents sit on the same 32px
         gutter as the header's logo and nav above them. `50%` rather than `50vw` for the
         same reason the width used it — a fixed element's containing block excludes the
         scrollbar and `vw` does not. */
      padding-block: calc(var(--da-space-8) + var(--da-search-panel-bleed)) var(--da-space-4);
      padding-inline: max(
        var(--da-search-panel-inset, 16px),
        50% - var(--da-search-panel-max-width, 1856px) / 2
      );

      /* The panel's visible top edge, and why the bleed costs nothing at rest.
         dropFromHeader animates this same polygon and hands back to this exact value on
         the frame it stops. A polygon rather than `inset()` because only the top may
         cut — the other three sit outside the box so the shadow survives, and `inset()`
         takes no negative lengths. */
      clip-path: polygon(
        -100px var(--da-search-panel-bleed),
        calc(100% + 100px) var(--da-search-panel-bleed),
        calc(100% + 100px) calc(100% + 100px),
        -100px calc(100% + 100px)
      );
    }

    /* Below the header's bottom edge, above everything else on the page: the island
       sits inside .section-header (z-index 10), so a fixed child of it paints over the
       page content and the USP banner without portalling. */
    .da-desktop-search-shell__scrim {
      position: fixed;
      inset: var(--da-search-scrim-top, 0) 0 0;
      background: color-mix(in srgb, var(--da-color-charcoal-500) 70%, transparent);
      backdrop-filter: blur(8px);
      z-index: 19;
    }

    .da-desktop-search-shell__columns {
      display: flex;
      gap: 36px;
    }

    /* Cards take the remaining width; both lists are width:100% by default, which would
       otherwise split the panel 50/50 and strand "Show all results" mid-panel.
       min-width:0 also keeps this column's width independent of the cards inside it,
       which is what makes measuring it non-circular. */
    .da-desktop-search-shell__products {
      flex: 1;
      min-width: 0;
    }

    /* Terms render left of the products per the design, but DOM order stays
       products-first because the shared item ids make arrow-key order follow it. Each
       column carries its own labelled role="group", so the split is announced. */
    .da-desktop-search-shell__columns > :global(.da-desktop-search-shell__terms) {
      order: -1;
      /* 240, not 220: less the row's 12px side padding that leaves 216 for a 16px
         "POPULAR SEARCHES", which measures ~197 and wrapped at 220. The 20px it costs
         the products column is well inside one card's 187px step. */
      flex: 0 0 240px;
    }

    .da-desktop-search-shell__empty {
      margin: 0;
      color: var(--da-color-charcoal-500);
      font-family: var(--da-font-family-primary);
      font-size: var(--da-font-size-md);
    }
  }
</style>
